import type { AxiosRequestConfig } from "axios";
import type { ResponseDataList } from "@/api/model/baseModel";

import request from "@/plugins/axios";

import OrderEnum from "@/api/order/enums";

const isDev =
  process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test";
const url = !isDev ? "" : "/api";

/**
 * 订单详情类型（用于 /system/task/taskList 接口返回）
 */
export interface MyOrderItemType {
  id: number;
  datasetId: number;
  /**
   * 是否有评估报告（ 0：无，1：有）
   */
  isAssessmentReport: 0 | 1;
  datasetName: string;
  price: string;
  taskCode: string;
  createTime: number;
  fileUrl: string;
  /**
   * 1: 处理中、0: 交易成功、2: 已退款 3：退款中 10:待审核 11:已付款 12:已拒绝
   */
  status: 1 | 2 | 0 | 3 | 10 | 11 | 12;
  /**
   * 是否可以退款
   */
  showRefundFlag: boolean;
  /**
   * 加密的私钥
   */
  privateKey: string;
  pattern: string;
  datasetUser: number;
  unUsed: number;
  encryptFileUrl: string;
  serverPublicKey: string;
  serverUrl: string;
  encryptFileHash: string;
  publishStatus: 0 | 1 | 2 | 3;
  datasetUserName: string;
  auditExpireTime: number;
  datasetVersion: 1 | 2; //1-下载不需要验证hash 2-下载需要验证hash
  taskReady: 0 | 1;
  clientDownloadType: string;
  clientDownloadUrl: string | null;
}

/**
 * 查询用户购买的订单
 */
export function getMyOrder(options: {
  pageNo: number;
  pageSize: number;
  name?: string;
  taskIdList?: number[];
}) {
  const config: AxiosRequestConfig = {
    url: `${url}${OrderEnum.MY_ORDER_LIST}`,
    method: "post",
    data: options,
  };
  return request<ResponseDataList<MyOrderItemType[]>>(config);
}
/**
 * 新购买的数据点击下载后修改状态
 */
export function useMyOrder(orderCode: string) {
  const config: AxiosRequestConfig = {
    url: `${url}${OrderEnum.USE_ORDER}`,
    method: "post",
    data: {
      orderType: 0, //数据集订单
      orderCode,
    },
  };
  return request<boolean>(config);
}
/**
 * 轮询未读消息
 */
export function getUnreadMessage() {
  const config: AxiosRequestConfig = {
    url: `${url}/unused/order/query`,
    method: "post",
  };
  return request<{
    totalCount: number;
    datasetOrderCount: number;
    apiOrderCount: number;
    orderNoticeList: string[];
  }>(config);
}
export function getMyOrderDetails(id: number) {
  const config: AxiosRequestConfig = {
    url: `${url}${OrderEnum.MY_ORDER_DETAIL}`,
    method: "post",
    data: { id },
  };
  return request<{
    id: number;
    datasetId: number;
    datasetName: string;
    createTime: number;
    taskCode: string;
    price: number;
    payWay: "1" | "2" | "3";
    pattern: string;
    datasetHash: string;
    isAssessmentReport: 0 | 1;
    datasetSize: number;
    assessmentMethodDTOList: {
      assessmentMethodId: number;
      assessmentMethod: string;
      templateHtml: string;
      assessmentReport: string;
      estimateTaskId: string;
    }[];
    description: string;
  }>(config);
}

/**
 * 平台介入（投诉订单）接口
 */
export function putFeedback(options: { id: number; desc: string }) {
  const config: AxiosRequestConfig = {
    url: `${url}${OrderEnum.SUBMIT_FEEDBACK}`,
    method: "post",
    data: options,
  };
  return request(config);
}

/**
 * 查询数据集的算法参数
 */
export function getForwardResult(datasetId: number) {
  const config: AxiosRequestConfig = {
    url: `${url}${OrderEnum.GET_FORMARD_RESULT}`,
    method: "post",
    data: { datasetId },
  };
  return request<string>(config);
}

/**
 * 下订单
 */
export function putTask(options: {
  datasetId: number;
  algorithmId: number;
  argsJson: string;
  credential: string;
  credentialSecret: string;
  forwardSig?: string;
  openId: string;
  linkFlagParams: string;
}) {
  const config: AxiosRequestConfig = {
    url: `${url}${OrderEnum.PUT_DATA}`,
    method: "post",
    data: {
      ...options,
      payWay: 3, // 支付类型 1-网银支付 2-支付宝支付 3-微信支付
    },
    withCredentials: true,
  };
  return request<string>(config);
}

// 退款数据
export function refundOrder(id: number) {
  const config: AxiosRequestConfig = {
    url: `${url}${OrderEnum.MY_ORDER_REFUND}`,
    method: "post",
    data: {
      taskId: id,
    },
  };
  return request(config);
}

export function getPayParameter(code: string) {
  const config: AxiosRequestConfig = {
    url: `${url}${OrderEnum.GET_CACHE_ORDER}`,
    method: "post",
    data: {
      orderCode: code,
    },
  };
  return request(config);
}
export function getPayParameterNew(code: string, taskCode: string) {
  const config: AxiosRequestConfig = {
    url: `${url}/task/create/payOrder`,
    method: "post",
    data: {
      code,
      taskCode,
    },
  };
  return request(config);
}
/**
 * 查询支付状态
 * @param { string } orderCode
 */
export function getPayState(orderCode: string) {
  const config: AxiosRequestConfig = {
    url: `${url}${OrderEnum.PAY_STATUS}`,
    method: "post",
    data: {
      orderCode,
    },
  };
  return request<string>(config);
}

/**
 * 查询数据集的最新订单
 * @param { number } datasetId 数据集ID
 * @returns 返回单个订单详情，类型与 taskList 接口一致
 */
export function queryNewestTask(datasetId: number) {
  const config: AxiosRequestConfig = {
    url: `${url}${OrderEnum.QUERY_NEWEST_TASK}`,
    method: "post",
    data: {
      datasetId,
    },
  };
  return request<MyOrderItemType>(config);
}
